import { merge } from "lodash";
import { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Box, Card, CardHeader, Link, Grid, Stack, Typography } from "@mui/material";
import { fNumber } from "../../../utils/formatNumber";
import { BaseOptionChart } from "../../charts";
import { alpha, styled } from "@mui/material/styles";
import { color, shadows } from "@mui/system";

function AppConversionRates(props) {
  const [refreshGraph, setRefreshGraph] = useState(false);
  const [types_prescribed_ordered, setTypes_prescribed_ordered] = useState([]);
  const [prescribedTranformData, setPrescribedTranformData] = useState([]);
  const [resistantTranformData, setResistantTranformData] = useState([]);
  const [prescXaxis, setPrescXaxis] = useState(0);
  const [radialBarSeries, setRadialBarSeries] = useState([70, 30]);
  const [donutBarSeries, setDonutBarSeries] = useState([70, 30]);
  const [labelPositions, setLabelPositions] = useState({ red: { x: 80, y: 35 }, green: { x: 40, y: 70 } });

  useEffect(() => {
    if (props.ab.greenPercent && props.ab.redPercent) {
      setRadialBarSeries([
        props.ab.redPercent,
        props.ab.greenPercent,
      ]);


      // Interpolated positioning for smooth 0-100% coverage
      function getInterpolatedPosition(percentage, isOuter = false) {
        // Define key positions every 5% around the radial bar arc
        // Based on { x: 120, y: 105 } being good for 10%
        const keyPositions = isOuter ? {
          0: { x: 145, y: 50 },    // Start (outer, further from center)
          5: { x: 125, y: 62 },    
          10: { x: 90, y: 85 },   // Outer equivalent of known good position
          15: { x: 73, y: 115 },  
          20: { x: 60, y: 150 },  
          25: { x: 60, y: 185 },  
          30: { x: 75, y: 225 },  
          35: { x: 100, y: 255 },   
          40: { x: 140, y: 275 },   
          45: { x: 185, y: 310 },   
          50: { x: 210, y: 320 },   // Half (outer)
          55: { x: 245, y: 310 },   
          60: { x: 270, y: 245 },   
          65: { x: 290, y: 215 },   
          70: { x: 300, y: 180 },   
          75: { x: 300, y: 143 },   
          80: { x: 285, y: 105 },   
          85: { x: 260, y: 80 },   
          90: { x: 220, y: 60 },   
          95: { x: 200, y: 50 },  
          100: { x: 190, y: 50 } // End position (outer)
        } : {
          0: { x: 145, y: 85 },   // Start (inner)
          5: { x: 140, y: 90 },   
          10: { x: 120, y: 105 },  // Known good position
          15: { x: 105, y: 130 },  
          20: { x: 95, y: 160 },  
          25: { x: 95, y: 190 },  
          30: { x: 110, y: 210 },  
          35: { x: 130, y: 230 },   
          40: { x: 160, y: 245 },   
          45: { x: 180, y: 255 },   
          50: { x: 210, y: 250 },   // Half
          55: { x: 230, y: 240 },   
          60: { x: 250, y: 210 },   
          65: { x: 260, y: 220 },   
          70: { x: 275, y: 210 },   
          75: { x: 270, y: 190 },   
          80: { x: 255, y: 120 },   
          85: { x: 235, y: 100 },   
          90: { x: 210, y: 85 },  
          95: { x: 194, y: 85 },  
          100: { x: 194, y: 85 } // End position
        };
        
        // Find the two closest key positions
        const keys = Object.keys(keyPositions).map(Number).sort((a, b) => a - b);
        
        let lowerKey = 0;
        let upperKey = 100;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (percentage >= keys[i] && percentage <= keys[i + 1]) {
            lowerKey = keys[i];
            upperKey = keys[i + 1];
            break;
          }
        }
        
        // Linear interpolation between the two positions
        const t = (percentage - lowerKey) / (upperKey - lowerKey);
        const lowerPos = keyPositions[lowerKey];
        const upperPos = keyPositions[upperKey];
        
        return {
          x: Math.round(lowerPos.x + (upperPos.x - lowerPos.x) * t),
          y: Math.round(lowerPos.y + (upperPos.y - lowerPos.y) * t)
        };
      }
  
      // Calculate positions for both bars using interpolated approach
      const redPos = getInterpolatedPosition(props.ab.redPercent, true); // Outer bar
      const greenPos = getInterpolatedPosition(props.ab.greenPercent, false); // Inner bar
      
      setLabelPositions({
        red: redPos,
        green: greenPos
      });
    }else {
      setRadialBarSeries([
        0,
        0,
      ]);
    }
  }, [props.ab.greenPercent, props.ab.redPercent]);

  useEffect(() => {
    if (types_prescribed_ordered && types_prescribed_ordered !== undefined) {
      // If data has changed, trigger graph refresh
      setRefreshGraph((prev) => !prev);
    }
  }, [types_prescribed_ordered]);

  useEffect(() => {
    if (props.ab.types_prescribed && props.ab.types_prescribed !== undefined) {
      const sum = props.ab.types_prescribed.reduce((a, b) => a + b, 0);
      setTypes_prescribed_ordered(
        props.ab.types_prescribed.sort((a, b) => b.y - a.y)
      );
      setRefreshGraph((prev) => !prev);
    }
  }, [props.ab.types_prescribed]);

  useEffect(() => {
    if (props.ab.types_prescribed && props.ab.total_12_month) {
      // const newData = props.ab.types_prescribed.map((item) => {
      //   const newY = (item.y / 100) * props.ab.total_12_month;
      //   return { ...item, y: newY };
      // });

      const newData = props.ab.types_prescribed.map((item) => {
        const newY = (item.y / 100) * props.ab.total_12_month;

        // If the value is less than 5, set `y` to 0 and indicate it's less than 5 in `label`.
        if (newY < 5) {
          return { ...item, y: 0, label: 'Count less than 5' };
        }

        return { ...item, y: newY };
      });

      setPrescribedTranformData(newData);

      const prescValue = props.ab.types_prescribed.map((item) => item.y);
      const maxVal = Math.max(...prescValue) * 1.3;

      setPrescXaxis(maxVal);
    }
  }, [props.ab.types_prescribed, props.ab.total_12_month]);

  useEffect(() => {
    if (props.ab.types_resistance) {

      const newData = props.ab.types_resistance.map((item) => {

        const newY = ((item.y * props.ab.resistanceTotal) / 100).toFixed(0);

        // If the value is less than 5, set `y` to 0 and indicate it's less than 5 in `label`.
        // if (newY < 5) {
        //   return { ...item, y: 0, label: 'Count less than 5' };
        // }

        return { ...item };
      });

      setResistantTranformData(newData);
    }
  }, [props.ab.types_resistance]);


  useEffect(() => {
      const greenPercent = props.ab.practice_green ? ((props.ab.practice_green / props.ab.total_12_month) * 100).toFixed(0) : 0;
      let redPercent = 0;
      if(props.ab.practice_red){
        redPercent = 100 - greenPercent;
      }
      setDonutBarSeries([parseFloat(greenPercent), parseFloat(redPercent)]);
  }, [props.ab.practice_green, props.ab.practice_red]);

  const chartOptionsPrescribe = merge(BaseOptionChart(), {
    tooltip: {
      marker: { show: false },
      x: {
        show: true,
        formatter: undefined,
        title: {
          formatter: (seriesName) => seriesName,
        },
      },
      y: {
        formatter: function (value) {
          const percentage = (value / props.ab.total_12_month) * 100;
          return Math.round(percentage) + "%";
        },
        title: {
          formatter: () => `Percentage of total: `,
        },
      },
    },
    chart: {
      type: "bar",
      height: 350,
      stacked: false,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        dataLabels: {
          position: "top",
        },
        barHeight: 20,
        borderRadius: 5,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: {
      formatter: function (val) {
        return fNumber(val);
      },
      enabled: true,
      offsetX: 45,
      style: {
        colors: ["#708090"],
        fontWeight: "normal",
      },
    },
    xaxis: {
      min: 0,
      max: prescXaxis,
      forceNiceScale: true,
      tickAmount: 5,
      title: {
        style: {
          color: "#22577A",
        },
      },
      labels: {
        show: true,
        style: {
          colors: "#22577A",
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        maxWidth: 100,
        style: {
          colors: "#22577A",
        },
      },
    },
  });

  const chartOptions = {
    chart: { type: "donut" },
    labels: ["", ""], // No labels needed
    colors: ["#216B3A", "#BC4024"], // Green and Red
    plotOptions: {
      pie: {
        donut: {
          size: "70%", // Proper ring size
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      style: { fontSize: "16px", fontWeight: "bold" },
    },
    tooltip: {
      enabled: true,
      style: {
        color: '#fff', // Makes tooltip text white
      },
      y: {
        formatter: function (val, opts) {
          // opts.seriesIndex: 0 for red, 1 for green
          if (opts.seriesIndex === 0) {
            return `Red Antibiotics: ${props.ab.practice_red}`;
          } else if (opts.seriesIndex === 1) {
            return `Green Antibiotics: ${props.ab.practice_green}`;
          }
          return val + "%";
        }
      }
    },
    legend: { show: false },
    stroke: { width: 0 },
    annotations: {
      position: "front",
      texts: [
        {
          text: "GPs in Ireland prescribed",
          x: "50%",
          y: "43%",
          textAnchor: "middle",
          fontSize: "14px",
          color: "black",
          fontWeight: "bold",
        },
        {
          text:  `${props.model.total} antibiotics in`,
          x: "50%",
          y: "50%",
          textAnchor: "middle",
          fontSize: "14px",
          color: "black",
          fontWeight: "bold",
        },
        {
          text: "the last 12 months",
          x: "50%",
          y: "57%",
          textAnchor: "middle",
          fontSize: "14px",
          color: "black",
          fontWeight: "bold",
        },
      ],
    },
  };

  const chartOptionsComparisons2 = {
    chart: { type: "radialBar" },
    colors: ["#BC4024", "#216B3A"], // Red and Green
    labels: [`${radialBarSeries[0]}%`, `${radialBarSeries[1]}%`],
    plotOptions: {
      radialBar: {
        hollow: { size: "40%" },
        track: { background: "transparent" },
        dataLabels: {
          show: false,
        },
      },
    },
  tooltip: {
    enabled: true,
    theme: "dark",
    style: {
      color: '#fff', // Makes tooltip text white
    },
    y: {
      formatter: function (val, opts) {
        // opts.seriesIndex: 0 for red, 1 for green
        if (opts.seriesIndex === 0) {
          return `Red Antibiotics: ${props.ab.red_resistance_count}`;
        } else if (opts.seriesIndex === 1) {
          return `Green Antibiotics: ${props.ab.green_resistance_count}`;
        }
        return val + "%";
      }
    }
  },
    annotations: {
      position: "front",
      texts: [
        {
          text: "% of tested urine ",
          x: "50%",
          y: "40%",
          textAnchor: "middle",
          fontSize: "12px",
          color: "black",
          fontWeight: "bold",
        },
        {
          text: "samples which were",
          x: "50%",
          y: "45%",
          textAnchor: "middle",
          fontSize: "12px",
          color: "black",
          fontWeight: "bold",
        },
        {
          text: "resistant against green",
          x: "50%",
          y: "50%",
          textAnchor: "middle",
          fontSize: "12px",
          color: "black",
          fontWeight: "bold",
        },
        {
          text: "and red antibiotics",
          x: "50%",
          y: "55%",
          textAnchor: "middle",
          fontSize: "12px",
          color: "black",
          fontWeight: "bold",
        },
      ],
    },
  };


  const chartOptionsResist = merge(BaseOptionChart(), {
    tooltip: {
      marker: { show: false },
      x: {
        show: true,
        // format: 'dd MMM',
        formatter: undefined,
        title: {
          formatter: (seriesName) => seriesName,
        },
      },
      y: {
        formatter: (value, opts) => {
          if (props.ab.denominator) {
            const item = resistantTranformData[opts.dataPointIndex];
            if (item.label === 'Count less than 5') {
              return item.label;
            }
            const actualCount = ((value * props.ab.denominator) / 100).toFixed(0);
            return fNumber(actualCount)
          }
        },
        title: {
          formatter: (seriesName) =>
            `Count of isolates showing resistance: `,
        },
      },
    },
    chart: {
      type: "bar",
      height: 350,
      stacked: false,
      stackType: "100%",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        dataLabels: {
          position: "top",
        },
        barHeight: 20,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: {
      formatter: function (value, { seriesIndex, dataPointIndex, w }) {
        const item = resistantTranformData[dataPointIndex];
        return item.label === 'Count less than 5' ? item.label : (value ? value.toFixed(0) : 0) + "%";
      },
      enabled: true,
      offsetX: 40,
      style: {
        colors: ["#708090"],
        fontWeight: "normal",
      },
      // offsetX: 50,
    },
    xaxis: {
      min: 0,
      max: 100,
      stepSize: 25,
      title: {
        text: "%",
        style: {
          color: "#22577A",
        },
      },
      labels: {
        show: true,
        style: {
          colors: "#22577A",
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          colors: "#22577A",
        },
      },
    },
  });

  const staticPrescribedData = [
    {
      x: "Amoxicillin",
      y: 450,
      fillColor: "#26734D",
      strokeColor: "#26734D"
    },
    {
      x: "Doxycycline",
      y: 380,
      fillColor: "#26734D",
      strokeColor: "#26734D"
    },
    {
      x: "Ciprofloxacin",
      y: 320,
      fillColor: "#cc3311",
      strokeColor: "#cc3311"
    },
    {
      x: "Penicillin V",
      y: 280,
      fillColor: "#26734D",
      strokeColor: "#26734D"
    },
    {
      x: "Azithromycin",
      y: 250,
      fillColor: "#cc3311",
      strokeColor: "#cc3311"
    }
  ];

  return (
    <Card sx={{ boxShadow: "none" }}>
      <br />
      <br />
      <Typography variant="h4" color="#22577A">
        Antibiotic resistance
      </Typography>
      <br />
      <Typography variant="subtitle3" color="#22577a">
        The Health Service Executive (HSE) Ireland has colour-categorised antibiotics into red and
        green with green identifying the antibiotics that should be preferred
        (see <Link href="https://www.hse.ie/eng/services/list/2/gp/antibiotic-prescribing/safe-prescribing/preferred-antibiotic-use-in-the-community-greenred-table.pdf">HSE</Link> ).
      </Typography>
      <Card sx={{ boxShadow: "none", padding: 2 }}>

        <Grid sx={{ display: 'flex', flexDirection: 'row' }} xs={12}>
          <Box sx={{ marginTop: 0.5, mx: '4px', width: 15, height: 15, backgroundColor: "#26734D" }}></Box>
          <Typography
            variant="subtitle3"
            color="#26734D"
            sx={{ fontWeight: "bold" }}
          >
            Green Antibiotics{" "}
          </Typography>
          <Typography variant="subtitle3" color="#22577a">
            &nbsp; are more effective, have fewer side effects and are less likely to lead
            to resistant infections.
          </Typography>
        </Grid>


        <Grid sx={{ display: 'flex', flexDirection: 'row' }} xs={12}>
          <Box sx={{ marginTop: 0.5, mx: '4px', width: 15, height: 15, backgroundColor: "#cc3311" }}></Box>
          <Typography
            variant="subtitle3"
            color="#cc3311"
            sx={{ fontWeight: "bold" }}
          >
            Red Antibiotics{" "}
          </Typography>
          <Typography variant="subtitle3" color="#22577a">
            &nbsp; should not be used in general practice unless absolutely necessary.
          </Typography>
        </Grid>
      </Card>
      <br />
      <br />
      <br />
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <ReactApexChart
            type="donut"
            series={donutBarSeries} // Green, Red
            options={chartOptions}
            height={350}
            key={refreshGraph}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <ReactApexChart
              type="radialBar"
              series={radialBarSeries} // Green, Red
              options={chartOptionsComparisons2}
              height={400}
              width={400}
              key={refreshGraph}
            />
            {/* Custom percentage labels positioned at bar ends */}
            <Box
              sx={{
                position: 'absolute',
                top: `${labelPositions.red.y}px`,
                right: `${labelPositions.red.x}px`,
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#BC4024',
                zIndex: 10,
              }}
            >
              {radialBarSeries[0]}%
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: `${labelPositions.green.y}px`,
                right: `${labelPositions.green.x}px`,
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#216B3A',
                zIndex: 10,
              }}
            >
              {radialBarSeries[1]}%
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Stack
        direction="row"
        justifyContent="space-evenly"
        alignItems="flex-end"
        spacing={6}
        mt={1}
      >
        <Grid container>
          <Grid item sm={12} md={6}>
            <Box sx={{ mx: 1, width: 500, height: 400 }} dir="ltr">
              <br />
              <Typography variant="h6" mt={10} color="#22577A">
                {" "}
                Most common types of antibiotics prescribed
              </Typography>{" "}
              <ReactApexChart
                type="bar"
                series={[
                  { data: types_prescribed_ordered.sort((a, b) => b.y - a.y) }
                ]}
                //series={[props.ab.types_prescribed]}
                options={chartOptionsPrescribe}
                key={refreshGraph}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box
              sx={{ mx: 1, my: 3, width: 500, height: 400 }}
              dir="ltr"
              id="resistance"
            >
              <Typography variant="h6" mt={12} color="#22577A">
                {" "}
                Urine samples showing resistance to:.{" "}
              </Typography>
              <ReactApexChart
                type="bar"
                series={[
                  { data: resistantTranformData.sort((a, b) => b.y - a.y) },
                ]}
                options={chartOptionsResist}
                key={refreshGraph}
              />
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </Card>
  );
}

import { connect } from "react-redux";

const mapState = (state) => ({
  ab: state.antibiotics.ab,
  model: state.antibiotics.generalAB,
});

const mapDispatch = ({ antibiotics: { getGeneralAB } }) => ({
  getGeneralAB: () => getGeneralAB(),
});

export default connect(mapState, mapDispatch)(AppConversionRates);
